import { useEffect } from "react";
import { MessageResponse } from "stream-chat";
import type { DefaultStreamChatGenerics } from "stream-chat-react";
import { MessageInput, useChannelStateContext } from "stream-chat-react";
import { InkleAIMessageInput } from "./InkleAIMessageInput";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { EntityBanner } from "./InkleAIChatHeaders";
import { useCurrentEntity } from "hooks/useCurrentEntity";
import { useAppSelector } from "hooks/useAppSelector";

export const InkleAIEmptyState = ({
  onExploreAllClick,
  prefilledMessage,
  onThreadMessageChange,
}: {
  onExploreAllClick?: () => void;
  prefilledMessage?: string;
  onThreadMessageChange?: (
    msg: MessageResponse<DefaultStreamChatGenerics>
  ) => void;
}) => {
  const { channel } = useChannelStateContext();
  const currentEntityId = useCurrentEntityId();
  const { entities } = useCurrentGroupContext();
  const entity = useCurrentEntity();
  const { showAIChannels, showOnlyAdminMessage } = useAppSelector(
    (state) => state.chatSearch
  );
  const aiMessageViewOnly = showAIChannels && !showOnlyAdminMessage;

  const usEntities = entities.filter(
    (e) => e.country === "US" || e.country_code === "US"
  );
  const defaultEntityId =
    usEntities.length === 1 ? usEntities[0].uuid : undefined;
  const entityId = currentEntityId || defaultEntityId;

  const selectedEntity = entities.find((e) => e.uuid === entityId);
  const entityName = selectedEntity?.name;

  const shouldShowBanner = !entity.name && !!entityName;

  useEffect(() => {
    const handleNewMessage = () => {
      if (channel.state.messages.length > 0 && onThreadMessageChange) {
        const lastMessage =
          channel.state.messages[channel.state.messages.length - 1];
        onThreadMessageChange(lastMessage as any);
      }
    };

    channel.on("message.new", handleNewMessage);
    channel.on("message.updated", handleNewMessage);

    return () => {
      channel.off("message.new", handleNewMessage);
      channel.off("message.updated", handleNewMessage);
    };
  }, [channel, onThreadMessageChange]);

  return (
    <div className="t-bg-neutral-0 t-rounded-tl-xl t-h-full t-flex t-flex-col t-border t-border-solid t-border-neutral-0 t-w-full">
      {shouldShowBanner && entityName && (
        <div className="t-flex-shrink-0">
          <EntityBanner entityName={entityName} />
        </div>
      )}
      {!aiMessageViewOnly && (
        <div className="t-flex-1">
          <MessageInput
            grow
            Input={() => (
              <InkleAIMessageInput
                entityId={entityId}
                key={prefilledMessage}
                onExploreAllClick={onExploreAllClick}
                prefilledMessage={prefilledMessage}
              />
            )}
          />
        </div>
      )}
    </div>
  );
};

export const LoadingScreen = () => {
  return (
    <div className="t-flex t-items-center t-justify-center t-h-full t-bg-neutral-0">
      <div className="t-text-text-30 t-p-4">Starting a new chat thread...</div>
    </div>
  );
};
